import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';
import COLORS from '../../constants/colors';

/** Bulk commands that arrive in `options` alongside real values. */
const BULK_ACTIONS = ['Select All', 'Clear All'];

/** Below this, a search box costs more room than it saves. */
const SEARCH_THRESHOLD = 8;

const bulkActionSx = (option: string) => ({
  fontWeight: 600,
  color: COLORS.PRIMARY_BLUE,
  borderBottom: option === 'Clear All' ? `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}` : undefined,
});

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  /** Called once per toggled value, exactly as a chip click would. */
  onToggle: (filter: string, selectedTab: any) => void;
  selectedTab: number | string;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  minWidth?: number;
}

/**
 * Compact replacement for a full row of filter chips. It drives the same
 * per-value toggle handler the chips used, so selection, persistence and the
 * Select All / Clear All commands all behave identically.
 */
export function MultiSelectFilter({
  label,
  options,
  selected,
  onToggle,
  selectedTab,
  color = 'info',
  minWidth = 190,
}: MultiSelectFilterProps) {
  const [query, setQuery] = React.useState('');

  const values = options.filter((option) => !BULK_ACTIONS.includes(option));
  const normalizedQuery = query.trim().toLowerCase();
  const showSearch = values.length > SEARCH_THRESHOLD;

  const visibleOptions = options.filter((option) => {
    // Hidden while searching: "Select All" acts on every value, not the matches,
    // which would be misleading next to a filtered list.
    if (BULK_ACTIONS.includes(option)) {
      return !normalizedQuery;
    }

    return !normalizedQuery || option.toLowerCase().includes(normalizedQuery);
  });
  const hasVisibleValues = visibleOptions.some((option) => !BULK_ACTIONS.includes(option));

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value as string[];
    // MUI hands back the whole array; toggling one option at a time means the
    // difference is a single value, which is what the handler expects.
    const added = next.filter((item) => !selected.includes(item));
    const removed = selected.filter((item) => !next.includes(item));

    [...added, ...removed].forEach((item) => onToggle(item, selectedTab));
  };

  const optionItems = visibleOptions.map((option) => {
    if (BULK_ACTIONS.includes(option)) {
      return (
        <MenuItem key={option} value={option} sx={bulkActionSx(option)}>
          {option}
        </MenuItem>
      );
    }

    return (
      <MenuItem key={option} value={option}>
        <Checkbox size="small" checked={selected.includes(option)} sx={{ p: '4px', mr: '6px' }} />
        <ListItemText primary={option} primaryTypographyProps={{ fontSize: '0.875rem' }} />
      </MenuItem>
    );
  });

  return (
    <FormControl size="small" sx={{ minWidth }}>
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={handleChange}
        onClose={() => setQuery('')}
        MenuProps={{
          PaperProps: { sx: { maxHeight: 360 } },
          // Let the search field keep focus instead of the Select's list.
          autoFocus: false,
        }}
        renderValue={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ fontSize: '0.875rem', color: selected.length ? COLORS.MAIN_DARK : COLORS.LIGHT_GRAY }}
            >
              {label}
            </Typography>
            {selected.length > 0 && (
              <Chip
                label={selected.length === 1 ? selected[0] : selected.length}
                size="small"
                color={color}
                sx={{ maxWidth: '140px', height: '20px', fontSize: '0.6875rem' }}
              />
            )}
          </Box>
        )}
      >
        {showSearch && (
          <ListSubheader sx={{ p: '8px', lineHeight: 'normal', backgroundColor: COLORS.WHITE }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={`Search ${label.toLowerCase()}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              // The Select treats keystrokes as type-ahead and would steal them.
              onKeyDown={(event) => event.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '18px', color: COLORS.LIGHT_GRAY }} />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>
        )}
        {optionItems}
        {!hasVisibleValues && (
          <MenuItem disabled>
            <ListItemText
              primary={normalizedQuery ? 'No matches' : 'No options'}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
